import React, { useCallback } from 'react';
import { View } from 'react-native';
import GridLayout from '../../../../components/GridLayout';
import { useSelector } from 'react-redux';
import OverviewLoader from '../../../Overview/components/OverviewComp/OverviewLoader';
import get from 'lodash/get';
import TopicTrendsOverTime from './TopicTrendsOverTime';
import TopicSummary from './TopicSummary';
import SentimentDistributionByTopic from './SentimentDistributionByTopic';
import SentimentDistribution from './SentimentDistribution';
import AggregateData from './AggregateData';
import ReviewsAndAverageRating from './ReviewsAndAverageRating';
import FeedbackSourceMix from './FeedbackSourceMix';
import TopicBySentiment from './TopicBySentiment';
import TopPainPointAndCompllimentChart from './TopPainPointAndCompllimentChart';
import SegmentRanker from './SegmentRanker';
import TopicHierarchy from './TopicHierarchy';
import TopicsBreakout from './TopicsBreakout';
import Recommendations from './Recommendations';
import TopAndBottomTopics from '../../../../components/Common/WidgetsCards/TopAndBottomTopics';

const Discovery = () => {
  const { discovery, aiInsightsLoading } = useSelector(e => e.insightsReducer);
  const widget_list = get(discovery, 'widgetList', []);
  const { filtersLoading, applied_filter_values, applied_filters } = useSelector(e => e.filterReducer);
  const timeperiodLabel = get(applied_filters, 'timePeriod[0].fullLabel', 'Last 90 days');

  const renderItem = useCallback(
    ({ item }) => {
      switch (item.type) {
        case 'TOPIC_TRENDS_OVER_TIME':
          return (
            <TopicTrendsOverTime
              timeperiodLabel={timeperiodLabel}
              itemData={item}
              applied_filter_values={applied_filter_values}
              applied_filters={applied_filters}
              widgetId={item?.widgetId}
              reduxData={get(discovery, item?.widgetId, {})}
            />
          );

        case 'TOPIC_SUMMARY':
          return (
            <TopicSummary
              timeperiodLabel={timeperiodLabel}
              itemData={item}
              applied_filter_values={applied_filter_values}
              applied_filters={applied_filters}
              widgetId={item?.widgetId}
              reduxData={get(discovery, item?.widgetId, {})}
            />
          );

        case 'FEEDBACK_SOURCE_MIX':
          return <FeedbackSourceMix
            timeperiodLabel={timeperiodLabel}
            applied_filter_values={applied_filter_values} itemData={item} />;
        case 'SENTIMENT_DISTRIBUTION_BY_TOPIC':
          return (
            <SentimentDistributionByTopic
              timeperiodLabel={timeperiodLabel}
              itemData={item}
              applied_filter_values={applied_filter_values}
              applied_filters={applied_filters}
              widgetId={item?.widgetId}
              reduxData={get(discovery, item?.widgetId, {})}
            />
          );
        case 'SENTIMENT_DISTRIBUTION':
          return <SentimentDistribution
            timeperiodLabel={timeperiodLabel}
            itemData={item} />;
        case 'TOP_PAIN_POINTS':
          return (
            <TopPainPointAndCompllimentChart
              timeperiodLabel={timeperiodLabel}
              itemData={item}
              applied_filter_values={applied_filter_values}
            />
          );
        case 'TOP_COMPLIMENT':
          return (
            <TopPainPointAndCompllimentChart
              timeperiodLabel={timeperiodLabel}
              itemData={item}
              applied_filter_values={applied_filter_values}
            />
          );
        case 'TOPIC_BY_SENTIMENT':
          return (
            <TopicBySentiment
              timeperiodLabel={timeperiodLabel}
              itemData={item}
              applied_filter_values={applied_filter_values}
            />
          );
        case 'TOP_BOTTOM_TOPICS':
          return <TopAndBottomTopics applied_filter_values={applied_filter_values} item={{...item,subTitle:timeperiodLabel}}/>
        case 'TOP_METRICS':
          return (
            <AggregateData
              timeperiodLabel={timeperiodLabel}
              itemData={item}
              applied_filter_values={applied_filter_values}
              applied_filters={applied_filters}
              widgetId={item?.widgetId}
              reduxData={get(discovery, item?.widgetId, {})}
            />
          );

        case 'REVIEWS_AVERAGE_RATING_OVER_TIME':
          return (
            <ReviewsAndAverageRating
              timeperiodLabel={timeperiodLabel}
              itemData={item}
              applied_filter_values={applied_filter_values}
              applied_filters={applied_filters}
              widgetId={'ai_analytics_reviews_and_average_rating'}
              reduxData={get(discovery, ['ai_analytics_reviews_and_average_rating'], {})}
            />
          );
        case 'TOP_METRICS':
          return (
            <AggregateData
              timeperiodLabel={timeperiodLabel}
              itemData={item}
              applied_filter_values={applied_filter_values}
              applied_filters={applied_filters}
              widgetId={item?.widgetId}
              reduxData={get(discovery, item?.widgetId, {})}
            />
          );

        case 'REVIEWS_AVERAGE_RATING_OVER_TIME':
          return (
            <ReviewsAndAverageRating
              timeperiodLabel={timeperiodLabel}
              itemData={item}
              applied_filter_values={applied_filter_values}
              applied_filters={applied_filters}
              widgetId={get(itemData, 'widgetId', '')}
              reduxData={get(discovery, [get(itemData, 'widgetId', '')], {})}
            />
          );

        case 'SEGMENT_RANKER':
          return (
            <SegmentRanker
              timeperiodLabel={timeperiodLabel}
              itemData={item}
              applied_filter_values={applied_filter_values}
              applied_filters={applied_filters}
              reduxData={discovery}
            />
          );
        case 'TOPIC_HIERARCHY':
          return (
            <TopicHierarchy
              timeperiodLabel={timeperiodLabel}
              itemData={item}
              applied_filter_values={applied_filter_values}
              applied_filters={applied_filters}
            />
          );

        case 'TOPIC_BREAKOUT':
          return (
            <TopicsBreakout
              timeperiodLabel={timeperiodLabel}
              itemData={item}
              applied_filter_values={applied_filter_values}
              applied_filters={applied_filters}
            />
          );
        case 'RECOMMENDATIONS':
          return (
            <Recommendations
              timeperiodLabel={timeperiodLabel}
              itemData={item}
              applied_filter_values={applied_filter_values}
              applied_filters={applied_filters}
            />
          );
        default:
          return null;
      }
    },
    [timeperiodLabel, applied_filter_values, discovery]
  );

  const renderContent = () => {
    if (filtersLoading || aiInsightsLoading || Object.keys(applied_filter_values).length === 0) {
      return <OverviewLoader />;
    }

    return <GridLayout data={widget_list.filter(e => e.isVisible)} gap={30} renderItem={renderItem} />;
  };

  return <View style={{ paddingHorizontal: 20 }}>{renderContent()}</View>;
};
export default Discovery;
